import { Controller } from "@hotwired/stimulus"

// Filters listing routes by label and navigates on selection. Used in the app navbar.
export default class extends Controller {
  static values = {
    routes: Array,
    noResultsText: String,
    shortcutMac: String,
    shortcutWin: String,
    shortcutHintMac: String,
    shortcutHintWin: String
  }

  static targets = ["input", "results", "resultItem", "shortcutBadge"]

  connect() {
    this.boundClose = this.close.bind(this)
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    this.boundFocusSearch = this.focusSearch.bind(this)
    document.addEventListener("keydown", this.boundFocusSearch)
    this.updateShortcutBadge()
  }

  updateShortcutBadge() {
    if (!this.hasShortcutBadgeTarget) return
    const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform)
    const badge = this.shortcutBadgeTarget
    badge.textContent = isMac ? (this.shortcutMacValue || "⌘K") : (this.shortcutWinValue || "Ctrl+K")
    badge.title = isMac ? (this.shortcutHintMacValue || "") : (this.shortcutHintWinValue || "")
  }

  disconnect() {
    this.removeCloseListeners()
    document.removeEventListener("keydown", this.boundFocusSearch)
  }

  // Ctrl+K (Windows/Linux) or Cmd+K (macOS) focuses the global search input and opens the dropdown.
  focusSearch(event) {
    if ((event.metaKey || event.ctrlKey) && event.key?.toLowerCase() === "k") {
      event.preventDefault()
      this.inputTarget.focus()
      this.open()
    }
  }

  search() {
    const q = (this.inputTarget.value || "").trim().toLowerCase()
    const routes = this.routesValue || []
    const filtered = q
      ? routes.filter((r) => (r.label || "").toLowerCase().includes(q))
      : routes

    this.renderResults(filtered, q)
    this.resultsTarget.hidden = false
  }

  renderResults(filtered, q) {
    const noResultsText = this.noResultsTextValue || "No results"
    if (filtered.length === 0) {
      this.resultsTarget.innerHTML = `<li class="px-3 py-2 text-sm text-slate-500" role="status">${this.escapeHtml(noResultsText)}</li>`
      return
    }
    this.resultsTarget.innerHTML = filtered
      .map(
        (r) =>
          `<li><a href="${this.escapeAttr(r.path)}" class="global-search-result block px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 focus:bg-slate-100 focus:outline-none" data-global-search-target="resultItem">${this.escapeHtml(r.label)}</a></li>`
      )
      .join("")
  }

  escapeHtml(s) {
    const div = document.createElement("div")
    div.textContent = s
    return div.innerHTML
  }

  escapeAttr(s) {
    return this.escapeHtml(s).replace(/"/g, "&quot;")
  }

  open() {
    this.search()
    requestAnimationFrame(() => {
      document.addEventListener("click", this.boundClose)
      document.addEventListener("keydown", this.boundHandleKeydown)
    })
  }

  close(event) {
    if (event && this.element.contains(event.target)) return
    this.resultsTarget.hidden = true
    this.removeCloseListeners()
  }

  removeCloseListeners() {
    document.removeEventListener("click", this.boundClose)
    document.removeEventListener("keydown", this.boundHandleKeydown)
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.resultsTarget.hidden = true
      this.inputTarget.blur()
      this.removeCloseListeners()
    }
  }
}
